import { DownloadCloud01 } from '@untitledui/icons';

import type {
  ImportProviderKind,
  ServiceImportStatus,
} from '@/hooks/integrations/useImportStatus';
import { buildImportCountLabel } from '@/hooks/integrations/useImportStatus';
import { Button } from '@/lib/base/buttons/button';
import { ProgressBarBase } from '@/lib/base/progress-indicators';
import { cx } from '@/utils/cx';

import type { ActivityItem } from './ActivityFeed';
import { ActivityFeed } from './ActivityFeed';

interface OnboardingImportStateProps {
  /** Current progress percentage (0-100) */
  progress: number;
  /** Estimated time remaining text, e.g., "3 mins left" */
  estimatedTimeRemaining: string | null;
  /**
   * Per-provider import status entries. Each entry surfaces its own row in
   * the progress card with provider-specific icon/label/active indicator.
   * Provider-agnostic: pass `useImportStatus().services` directly.
   */
  services: ServiceImportStatus[];
  /** Error message to display, if any */
  error: string | null;
  /** Callback when user clicks the dismiss button */
  onDismiss: () => void;
  /** Text for the dismiss button */
  dismissButtonText?: string;
  /** Whether to show the dismiss button (default: true) */
  showDismissButton?: boolean;
  /** Callback when user clicks "Retry" in error state */
  onRetry?: () => void;
  /** Recent activity items to display */
  recentItems?: ActivityItem[];
  /** Additional class names */
  className?: string;
}

// Aspirational per-kind labels (matches the original copy of the Gmail/GCal
// hard-coded version).  Keyed by `ServiceImportStatus.kind` so any new
// provider of the same kind picks up the same phrasing automatically.
const KIND_DESCRIPTION_LABELS: Partial<Record<ImportProviderKind, string>> = {
  email: 'Reading conversations',
  calendar: 'Reviewing calendar',
};

/** Pulsing activity indicator dot */
function ActivityIndicator({ active }: { active: boolean }) {
  return (
    <span
      className={cx(
        'size-2 rounded-full',
        active ? 'bg-utility-success-500 animate-pulse' : 'bg-utility-gray-300'
      )}
    />
  );
}

/** One row in the progress card — provider icon + descriptive label + live count + activity dot. */
function ServiceStatusRow({ service }: { service: ServiceImportStatus }) {
  const description =
    KIND_DESCRIPTION_LABELS[service.kind] ?? service.displayName;
  const countLabel = buildImportCountLabel(service);
  return (
    <div
      className='flex items-center gap-2'
      data-testid={`onboarding-import-${service.providerName}-status`}
    >
      {service.iconPath ? (
        <img
          src={service.iconPath}
          alt={service.displayName}
          className='h-5 w-5 shrink-0'
        />
      ) : (
        <span
          role='img'
          aria-label={service.displayName}
          className='h-5 w-5 shrink-0 rounded-[4px] bg-neutral-200 dark:bg-neutral-700'
        />
      )}
      <p className='text-sm text-primary truncate flex-1'>{description}</p>
      {countLabel && (
        <span className='shrink-0 text-xs text-tertiary tabular-nums'>
          {countLabel}
        </span>
      )}
      {service.active && <ActivityIndicator active={true} />}
    </div>
  );
}

/**
 * Error state component
 */
function ErrorState({
  error,
  onRetry,
  onDismiss,
  showDismissButton = true,
  dismissButtonText,
}: {
  error: string;
  onRetry?: () => void;
  onDismiss: () => void;
  showDismissButton?: boolean;
  dismissButtonText: string;
}) {
  return (
    <div
      className='flex flex-col items-center text-center'
      data-testid='onboarding-import-error'
    >
      <div className='mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-utility-error-100'>
        <svg
          className='h-6 w-6 text-utility-error-600'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
          />
        </svg>
      </div>
      <h3 className='text-lg font-semibold text-primary mb-2'>
        Something went wrong
      </h3>
      <p className='text-sm text-tertiary mb-6 max-w-sm'>{error}</p>
      <div className='flex items-center gap-3'>
        {onRetry && (
          <Button color='primary' size='md' onClick={onRetry}>
            Retry
          </Button>
        )}
        <a
          href='mailto:support@ahoy.so'
          className='text-sm font-medium text-brand-secondary hover:text-brand-secondary_hover underline underline-offset-2'
        >
          Get help
        </a>
      </div>
      {showDismissButton && (
        <button
          type='button'
          onClick={onDismiss}
          className='mt-4 text-sm text-tertiary hover:text-secondary transition-colors'
          data-component='action-button'
          data-instance='skip-onboarding'
        >
          {dismissButtonText}
        </button>
      )}
    </div>
  );
}

/**
 * OnboardingImportState - Displays the "Getting to know your business" onboarding UI
 *
 * Provider-agnostic: iterates `services` to render one row per active
 * integration (Gmail, Test Email, Google Calendar, future providers).
 */
export function OnboardingImportState({
  progress,
  estimatedTimeRemaining,
  services,
  error,
  onDismiss,
  dismissButtonText,
  showDismissButton = true,
  onRetry,
  recentItems,
  className,
}: OnboardingImportStateProps) {
  // Use different default text for error state vs normal state
  const effectiveDismissButtonText =
    dismissButtonText ??
    (error ? 'Skip to Dashboard' : 'Continue exploring ahoy');

  if (error) {
    return (
      <div
        className={cx(
          'flex flex-col items-center justify-center py-16 px-6',
          className
        )}
        data-testid='onboarding-import-state'
        data-component='onboarding-import'
        data-state='error'
      >
        <ErrorState
          error={error}
          onRetry={onRetry}
          onDismiss={onDismiss}
          showDismissButton={showDismissButton}
          dismissButtonText={effectiveDismissButtonText}
        />
      </div>
    );
  }

  return (
    <div
      className={cx(
        'flex flex-col items-center justify-center py-6 px-4',
        className
      )}
      data-testid='onboarding-import-state'
      data-component='onboarding-import'
      data-state='loading'
    >
      {/* Title */}
      <h1
        className='text-lg font-semibold text-primary text-center mt-4'
        data-testid='onboarding-import-title'
        data-component='onboarding-import-title'
      >
        Getting to know your business
      </h1>

      {/* Subtitle */}
      <p className='text-sm text-tertiary text-center mt-2 max-w-sm'>
        Analyzing your emails and contacts to identify follow-ups and deals.
      </p>

      {/* Progress card */}
      <div
        className='mt-4 w-full max-w-sm rounded-xl border border-border-secondary bg-bg-primary shadow-sm'
        data-testid='onboarding-import-progress-card'
        data-component='onboarding-import-progress-card'
      >
        {/* Card header */}
        <div className='flex items-center gap-2 px-3 py-2 border-b border-border-secondary'>
          <DownloadCloud01 className='size-[18px] shrink-0 text-tertiary' />
          <span className='text-sm font-medium text-primary'>
            Importing your data
          </span>
        </div>

        {/* Progress section */}
        <div className='px-3 py-3'>
          <div className='flex items-center gap-3 mb-3'>
            <ProgressBarBase
              value={progress}
              min={0}
              max={100}
              className='flex-1'
            />
            {estimatedTimeRemaining && (
              <span className='shrink-0 text-xs text-tertiary tabular-nums'>
                {estimatedTimeRemaining}
              </span>
            )}
          </div>

          {/* Status items - one per provider with jobs */}
          <div className='space-y-2'>
            {services.map(service => (
              <ServiceStatusRow key={service.providerName} service={service} />
            ))}
          </div>

          {/* Activity feed */}
          {recentItems && recentItems.length > 0 && (
            <>
              <div
                className='border-t border-border-secondary my-3'
                data-testid='onboarding-import-activity-separator'
                data-component='onboarding-import-activity-separator'
              />
              <ActivityFeed items={recentItems} />
            </>
          )}
        </div>
      </div>

      {/* Dismiss button */}
      {showDismissButton && (
        <Button
          color='secondary'
          size='sm'
          onClick={onDismiss}
          className='mt-4'
          data-testid='onboarding-import-skip-button'
          data-component='action-button'
          data-instance='skip-onboarding'
        >
          {effectiveDismissButtonText}
        </Button>
      )}
    </div>
  );
}

export default OnboardingImportState;
